import { Input, Select } from '@use-ui/bootstrap3'
import { useActions, useForm } from '@use-ui/hooks'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { Button } from '../Button/Button'
import '../Forms/forms.scss'
import { Metadata, MetadataActionRequestActionEnum, ServiceType } from '@jibo-cloud/qas-client'
import { serviceTypeOptions } from '../../model/Model'
import { getMetadataService, saveMetadataService } from '../../services/qas'

export function MetadataPage () {
  const [metadataList, setMetadataList] = useState<Metadata[]>([])

  const [selectedMeta, setSelectedMeta] = useState<Metadata | null>(null)

  const [statusMessage, setStatusMessage] = useState<String | ''>('[]')

  useEffect(() => {
    loadMetadata()
  }, [])

  const loadMetadata = async () => {
    await getMetadataService().then((res: Metadata[]) => res && setMetadataList(res))
  }

  useEffect(() => {
    if (selectedMeta && !metadataList.some((m) => m.id === selectedMeta.id)) {
      setSelectedMeta(null)
    }
  }, [metadataList])

  return (
    <div className="user-page">
      <MetaTable metadata={metadataList} selectMeta={setSelectedMeta} loadMetadata={loadMetadata} />
      <MetaDetails meta={selectedMeta} loadMetadata={loadMetadata} setStatusMessage={setStatusMessage}/>
      <div className="metaDataStatus">{statusMessage}</div>
    </div>
  )
}

function MetaTable ({ metadata, selectMeta, loadMetadata }) {
  const { action } = useActions()

  const addNewMeta = action(
    async () => {
      await saveMetadataService(MetadataActionRequestActionEnum.Insert, {
        appName: 'Meta Name',
        appId: 'Meta ID',
        appVersion: '1.0.0',
        serviceType: ServiceType.Luis
      })
      return loadMetadata()
    }
  )

  return (
    <div className="top-panel metadataList">
      <div className="top-panel-title">Metadata</div>

      <table className="data" cellPadding="0" cellSpacing="0">
        <thead>
          <tr>
            <td>#</td>
            <td style={{ width: '30%' }}>Name</td>
            <td style={{ width: '30%' }}>Service Type</td>
            <td style={{ width: '30%' }}>Tags</td>
          </tr>
        </thead>
        <tbody>
          {metadata.map((m) => (
            <tr key={m.id} onClick={() => selectMeta(m)}>
              <td>{formatMetaId(m)}</td>
              <td>{m.appName}</td>
              <td>{m.serviceType}</td>
              <td>{formatTags(m)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <Button onClick={addNewMeta} disabled={addNewMeta.progress}>
        Add New Metadata
      </Button>
    </div>
  )
}

type MetaFields = {
  appName: string;
  appId: string;
  appVersion: string;
  tags: string;
  serviceType: ServiceType;
  QnAMakerAppId: string;
  QnAMakerEndpointKey: string;
  QnAMakerEndpoint: string;
  luisSubscriptionKey: string;
  luisEndpoint: string;
  confidenceMinThreshold: string;
  MintEndpoint: string;
  MintBasicAuthToken: string;
  netParser: string;
};

function MetaDetails ({ meta, loadMetadata, setStatusMessage }: { meta: Metadata, loadMetadata: any, setStatusMessage: any }) {
  // TODO update definition of Metadata in QAS client and use the updated QAS client
  if (meta) {
    const tags = (meta as any).tags
    if (!tags) {
      (meta as any).tags = []
    }
  }

  const form = useForm<MetaFields>(meta as MetaFields)

  useEffect(() => {
    if (meta) {
      form.updateValues(meta)
      setStatusMessage('[]')
    }
  }, [meta])

  const deleteMeta = form.action(async () => {
    await saveMetadataService(MetadataActionRequestActionEnum.Delete, meta)
    return await loadMetadata()
  })

  const saveMeta = form.action(
    async (data) => {
      let tagsArray = []
      if (data.tags) {
        tagsArray = data.tags.split(',').map(function (item) {
          return item.trim()
        })
      }
      (data as any).tags = tagsArray
      console.log('Saving metadata:', data)
      await saveMetadataService(MetadataActionRequestActionEnum.Update, {
        id: meta.id,
        ...data
      })
      setStatusMessage('[Metadata saved.]')
      return await loadMetadata()
    }
  )

  if (!meta) {
    return (
      <div className="top-panel metadata">
        <div className="top-panel-title">Select Metadata</div>
      </div>
    )
  }

  return (
    <div className="top-panel metadata">
      <div className="top-panel-title">Metadata {formatMetaId(meta)}</div>

      <div className="data-form">
        <Input label="App Name" field={form.fields.appName} required />
        <Input label="App ID" field={form.fields.appId} required />
        <Input label="App Version" field={form.fields.appVersion} required />
        <Input label="App Tags" field={form.fields.tags} />
        <Select label="Service Type" field={form.fields.serviceType} options={serviceTypeOptions} required />
        {form.fields.serviceType.getValue() === ServiceType.Qnamaker && <Input label="QnAMaker appId" field={form.fields.QnAMakerAppId} />}
        {form.fields.serviceType.getValue() === ServiceType.Qnamaker && <Input label="QnAMaker endpointKey" field={form.fields.QnAMakerEndpointKey} />}
        {form.fields.serviceType.getValue() === ServiceType.Qnamaker && <Input label="QnAMaker endpoint" field={form.fields.QnAMakerEndpoint} />}
        {form.fields.serviceType.getValue() === ServiceType.Luis && <Input label="LUIS subscriptionKey" field={form.fields.luisSubscriptionKey} />}
        {form.fields.serviceType.getValue() === ServiceType.Luis && <Input label="LUIS endpoint" field={form.fields.luisEndpoint} />}
        {form.fields.serviceType.getValue() === ServiceType.Luis && <Input label="Confidence min threshold" field={form.fields.confidenceMinThreshold} />}
        {form.fields.serviceType.getValue() === ServiceType.Mint && <Input label="Mint endpoint" field={form.fields.MintEndpoint} />}
        {form.fields.serviceType.getValue() === ServiceType.Qnamaker && <Input label="Mint BasicAuth token" field={form.fields.MintBasicAuthToken} />}
        {form.fields.serviceType.getValue() === ServiceType.Jiboparser && <Input label="NET parser" field={form.fields.netParser} />}
      </div>

      <Button onClick={deleteMeta} disabled={deleteMeta.progress}>
        Delete
      </Button>

      <Button onClick={() => { console.log('saveMeta'); saveMeta() }} >
        Save
      </Button>
    </div>
  )
}

function formatMetaId (meta: Metadata): string {
  return meta.id.substring(meta.id.length - 5)
}

function formatTags (meta: Metadata): string {
  const tags = (meta as any).tags
  let tagsAsString = ''
  if (tags) {
    if (Array.isArray(tags)) {
      tagsAsString = tags.join(',')
    }
  } else {
    tagsAsString = ''
  }
  return tagsAsString
}
